import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { Field, FormSection, ErrorMessage } from '@atlaskit/form';
import { Checkbox } from '@atlaskit/checkbox';
import { RadioGroup } from '@atlaskit/radio';
import Select, { ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import TextArea from '@atlaskit/textarea';
import { capitalize, defaultTo, startCase, toUpper } from 'lodash';
import React, { useState, useEffect, FormEvent } from 'react';
import {
  InvoiceGroup,
  Maybe,
  Offering,
  SyntheticPricingPlan,
  TransactionAccount,
  Promotion,
  BillingFrequency,
  generatedTypesNew,
  PricingType,
} from '@css-xp/graphtypes';
import { StatuspageOfferingName } from '@atlassian/cssxp-types-entitlement';
import { PricingPlanType, InfoMessage, OfferingName } from '@atlassian/cssxp-typescommon';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import {
  InvoiceGroupFormField,
  SelectPromotionFormField,
  ErrorComponents,
  InfoPanel,
  useFeatureToggle,
} from '@css-xp/base';
import { fetchAvailableOfferingsForEntitlement } from '../../../../entitlement-page/src/queries/fetch-available-offerings-for-entitlement';
import { fetchAvailablePromotionsForEntitlementQuery } from '../../../../entitlement-page/src/queries/fetch-available-promotions-for-entitlement';
import intersectionBy from 'lodash/intersectionBy';
import { getAppropriatePromotions } from '@atlassian/temp-helper';
import '../../annual-subscription/src/entitlement-annual-subscription-pane';
import { QueryResult, useQuery } from '@apollo/client';
import { isSkipTrialEligibleV2 } from '@atlassian/cssxp-utils-entitlement';
import { isConfluenceProduct, isJiraProduct, isProductSkipTrialEligible } from '@atlassian/cssxp-utils-product';
import {
  ENABLE_SUPPORT_TICKET_AND_NOTE,
  ENABLE_ENTITLEMENT_SKIP_TRIAL,
  ENABLE_ENTITLEMENT_GRANT_PROMOTIONS,
} from '@atlassian/cssxp-utils-generic';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';

interface OfferingSelectItem {
  label: string;
  value: {
    id: string;
  };
}

interface SelectItem {
  label: string;
  value: string;
}

interface PricingOptionsType {
  name?: string;
  value?: string;
  label?: string;
  isDisabled?: boolean;
  testId?: string;
}

const CommercialPlan: SyntheticPricingPlan[] = [
  {
    type: 'COMMERCIAL',
    status: 'ACTIVE',
    discountPercentage: 0,
  },
];

const billingFrequencyOptions: SelectItem[] = [
  { label: capitalize(BillingFrequency.ANNUAL), value: BillingFrequency.ANNUAL },
  { label: capitalize(BillingFrequency.MONTHLY), value: BillingFrequency.MONTHLY },
];

const MAX_LENGTH = 100;

interface Props {
  entitlement: EntitlementDetails;
  transactionAccount: Maybe<TransactionAccount>;
  pricingPlanFlag?: boolean;
  withPromotionFlag?: boolean;
  withReactivateEntitlementFlag?: boolean;
}

export const EntitlementChangePlanForm = ({
  entitlement,
  transactionAccount,
  pricingPlanFlag = false,
  withPromotionFlag = false,
  withReactivateEntitlementFlag = false,
}: Props) => {
  const [availablePricingPlan, setAvailablePricingPlan] = useState<any[]>([]);
  const [availableOfferingsData, setAvailableOfferings] = useState<Partial<QueryResult>>({ loading: true });
  const [hasEligiblePricingPlan, setHasEligiblePricingPlan] = useState(false);
  const [defaultPricingPlan, setDefaultPricingPlan] = useState<string | undefined | null>('');
  const [isUserTierDisabled, setIsUserTierDisabled] = useState(true);
  const [isSkipTrialDisabled, setIsSkipTrialDisabled] = useState(true);
  const [isSkipTrialChecked, setIsSkipTrialChecked] = useState(false);
  const [billingFrequency, setBillingFrequency] = useState<string | undefined | null>(entitlement?.billingFrequency);

  const hasPaymentMethod = transactionAccount?.availablePaymentMethods?.totalCount ?? false;
  const offeringName = entitlement?.offering?.name ?? entitlement.edition;
  const [showNoPaymentMethodWarning, setShowNoPaymentMethodWarning] = React.useState(
    !hasPaymentMethod && !offeringName?.includes(capitalize(PricingType.FREE))
  );

  const scheduledQty = entitlement?.scheduledChanges?.chargeQuantities?.[0]?.quantity?.toString() ?? null;
  const [defaultOfferingQuantity, setDefaultOfferingQuantity] = useState<string>(
    scheduledQty ? scheduledQty : entitlement?.unitCount ? entitlement?.unitCount : '1'
  );
  const [availablePromotions, setAvailablePromotions] = useState<Maybe<Promotion>[]>(
    entitlement?.availablePromotions ?? []
  );
  const [cachedPromotions, setCachedPromotions] = useState<any>({});

  const [isEnabled] = useFeatureToggle();

  const getDefaultBillingGroup = (invoiceGroupId: string) => {
    return availableInvoiceGroups?.filter((invoiceGroup: InvoiceGroup) => {
      return invoiceGroup.id === invoiceGroupId;
    });
  };

  const { data: availablePromotionData, refetch } = useQuery<
    generatedTypesNew.FetchAvailablePromotionsForEntitlementQuery,
    generatedTypesNew.FetchAvailablePromotionsForEntitlementQueryVariables
  >(fetchAvailablePromotionsForEntitlementQuery, {
    variables: { id: entitlement.entitlementNumber },
    fetchPolicy: 'cache-first',
  });

  const isUpdateUnitsFeatureEnabled = true; //useSelector(getFeatureFlag(ENABLE_STATUSPAGE_UPDATE_UNITS));

  const scheduledChanges = entitlement?.scheduledChanges ?? null;
  const availableInvoiceGroups = (transactionAccount?.availableInvoiceGroups?.nodes ?? []) as Maybe<InvoiceGroup[]>;
  const defaultBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId;
  const hasSingleInvoiceGroup = availableInvoiceGroups?.length === 1;
  const scheduledBillingInvoiceGroupId = scheduledChanges?.scheduledBillingInvoiceGroupId;
  const defaultInvoiceGroupPricingPlan = scheduledBillingInvoiceGroupId
    ? getDefaultBillingGroup(scheduledBillingInvoiceGroupId)?.[0]?.shipToPriceEligibility
    : defaultBillingGroupId
    ? getDefaultBillingGroup(defaultBillingGroupId)?.[0]?.shipToPriceEligibility
    : hasSingleInvoiceGroup
    ? availableInvoiceGroups?.[0]?.shipToPriceEligibility
    : undefined;

  const [selectedInvoiceGroupPricingPlan, setSelectedInvoiceGroupPricingPlan] = useState(
    defaultInvoiceGroupPricingPlan
  );

  const getPricingEligibility = (
    pricingOptions: PricingOptionsType[],
    selectedInvoiceGroupPricingPlanValue: string | null | undefined
  ) => {
    let eligiblePricingPlan = null;
    const offeringPricingPlans = pricingOptions.map((pricingOption: PricingOptionsType) => pricingOption.value);
    const txAccountPricingPlan = (transactionAccount?.billToPartyPricingEligibilty ?? '')?.toUpperCase();
    const invoiceGroupPricingPlan = (selectedInvoiceGroupPricingPlanValue ?? '')?.toUpperCase();

    // Set final pricing plan to txa pricing plan
    // Default fallback case: always set it to COMMERCIAL
    if (
      invoiceGroupPricingPlan &&
      invoiceGroupPricingPlan !== PricingPlanType.COMMERCIAL &&
      offeringPricingPlans.includes(invoiceGroupPricingPlan)
    ) {
      eligiblePricingPlan = invoiceGroupPricingPlan;
    } else if (
      txAccountPricingPlan &&
      txAccountPricingPlan !== PricingPlanType.COMMERCIAL &&
      offeringPricingPlans.includes(txAccountPricingPlan)
    ) {
      eligiblePricingPlan = txAccountPricingPlan;
    } else {
      eligiblePricingPlan = PricingPlanType.COMMERCIAL;
    }

    setHasEligiblePricingPlan(true);
    return eligiblePricingPlan;
  };

  const setDefaultPricingPlanDetails = (
    pricingOptions: PricingOptionsType[],
    selectedInvoiceGroupPricingPlanValue: string | null | undefined
  ) => {
    const selectedPricingPlan = defaultTo(
      getPricingEligibility(pricingOptions, selectedInvoiceGroupPricingPlanValue),
      null
    );
    const defaultPricingPlanType =
      selectedPricingPlan ??
      capitalize(
        entitlement?.scheduledChanges?.pricingPlan
          ? entitlement?.scheduledChanges?.pricingPlan?.type
          : entitlement?.pricingPlan?.type ?? ''
      );
    let getDefaultPricingPlan;

    // If only 1 synthetic pricing plan is available, then selecting it as default value.
    if (pricingOptions.length === 1) {
      getDefaultPricingPlan = pricingOptions[0];
    } else {
      getDefaultPricingPlan = pricingOptions.find(
        (pricingPlan) => capitalize(pricingPlan.value) === capitalize(defaultPricingPlanType)
      );
    }

    setDefaultPricingPlan(selectedPricingPlan ?? getDefaultPricingPlan?.value);
  };

  const setSyntheticPricingPlan = (availableOfferingsData: Partial<QueryResult>) => {
    const syntheticPricingPlans = (availableOfferingsData?.data?.entitlementDetails?.offering
      ?.availableSyntheticPricingPlans ?? []) as SyntheticPricingPlan[];
    if (intersectionBy(syntheticPricingPlans, CommercialPlan, 'type').length === 0) {
      syntheticPricingPlans.push(CommercialPlan[0]);
    }
    const pricingOptions =
      syntheticPricingPlans?.map((plan, index) => {
        return {
          name: defaultTo(plan?.type, ''),
          value: defaultTo(plan?.type, ''),
          label: `${capitalize(defaultTo(plan?.type, ''))} ${
            plan?.discountPercentage >= 0 && `(${plan?.discountPercentage}% off)`
          }`,
          isDisabled: plan?.status !== 'ACTIVE',
          testId: `pricing-plan-grp-${index}`,
        };
      }) ?? [];

    setAvailablePricingPlan(pricingOptions);
    setDefaultPricingPlanDetails(pricingOptions, selectedInvoiceGroupPricingPlan);
  };

  useEffect(() => {
    (async () => {
      const offerings = await graphqlClient().query({
        query: fetchAvailableOfferingsForEntitlement,
        variables: { id: entitlement.id },
        fetchPolicy: 'no-cache',
      });

      setSyntheticPricingPlan(offerings);

      // Enable user-tier/quantity for Audience-Specific SP offering.
      const offeringWithChanges: Offering = scheduledChanges?.offering ?? offerings?.data?.entitlementDetails?.offering;
      if (offeringWithChanges?.name === StatuspageOfferingName.AUDIENCE_SPECIFIC) {
        setIsUserTierDisabled(false);
      }
      setAvailableOfferings({ ...offerings });
    })();
  }, []);

  useEffect(() => {
    if (availablePromotionData?.entitlementDetails?.availablePromotions) {
      setAvailablePromotions(availablePromotionData?.entitlementDetails?.availablePromotions);
    }
  }, [availablePromotionData]);

  useEffect(() => {
    (async () => {
      if (!isSkipTrialDisabled) {
        const intendedBillingPeriodState = isSkipTrialChecked ? 'PAID' : 'TRIAL';
        if (!cachedPromotions[intendedBillingPeriodState]) {
          const fetchedPromotions = await refetch({
            intendedBillingPeriodState,
          });
          setCachedPromotions((prevState: any) => {
            return {
              ...prevState,
              [intendedBillingPeriodState]: fetchedPromotions?.data,
            };
          });
        } else {
          setAvailablePromotions(cachedPromotions[intendedBillingPeriodState]?.entitlementDetails?.availablePromotions);
        }
      }
    })();
  }, [isSkipTrialDisabled, isSkipTrialChecked, cachedPromotions, refetch]);

  if (availableOfferingsData.loading && !availableOfferingsData.data) return <LoadingIndicator />;
  if (
    availableOfferingsData.error &&
    (!availableOfferingsData.data || !availableOfferingsData.data.entitlementDetails)
  ) {
    return <ErrorComponents.GenericErrorPage error={availableOfferingsData.error} />;
  }
  if (!availableOfferingsData.data) {
    return null;
  }

  const { entitlementDetails: statuspage } = availableOfferingsData.data;
  const { availableOfferings = [], offering } = statuspage;

  const availableOfferingOptions =
    /*// @ts-ignore */
    availableOfferings?.map((availableOffering: Offering) => ({
      label: defaultTo(availableOffering.name, ''),
      value: {
        id: availableOffering.id,
      },
    })) ?? ([] as OfferingSelectItem[]);

  if (offering && availableOfferingOptions) {
    availableOfferingOptions.unshift({
      label: offering.name,
      value: {
        id: offering.id,
      },
    });
  }

  const defaultOfferingOption = availableOfferingOptions.find(
    (availableOffering: OfferingSelectItem) =>
      availableOffering.value.id ===
      (scheduledChanges?.offering ? scheduledChanges.offering?.id : statuspage.offering?.id)
  );

  const updateFieldBasedOnOffering = (selectedId: string | undefined) => {
    const availablePricingPlanForOffering = ((selectedId === statuspage.offering?.id
      ? statuspage.offering?.availableSyntheticPricingPlans
      : statuspage.availableOfferings?.find((offering: Offering) => offering?.id === selectedId)
          ?.availableSyntheticPricingPlans) ?? []) as SyntheticPricingPlan[];
    if (intersectionBy(availablePricingPlanForOffering, CommercialPlan, 'type').length === 0) {
      availablePricingPlanForOffering.push(CommercialPlan[0]);
    }
    const pricingOptions =
      availablePricingPlanForOffering.map((plan, index) => {
        return {
          name: defaultTo(plan?.type, ''),
          value: defaultTo(plan?.type, ''),
          label: `${startCase(defaultTo(plan?.type, ''))} ${
            plan?.discountPercentage >= 0 && `(${plan?.discountPercentage}% off)`
          }`,
          isDisabled: plan?.status !== 'ACTIVE',
          testId: `pricing-plan-grp-${index}`,
        };
      }) ?? [];

    const selectedOffering =
      statuspage.availableOfferings?.find((offering: Offering) => offering?.id === selectedId) ??
      (statuspage.offering.id === selectedId ? statuspage.offering : null);

    //Enable user-tier/quantity for Audience-Specific SP offering.
    if (selectedOffering.name === OfferingName.AUDIENCE_SPECIFIC) {
      setIsUserTierDisabled(false);
    } else {
      setDefaultOfferingQuantity('1');
      setIsUserTierDisabled(true);
    }

    setShowNoPaymentMethodWarning(!hasPaymentMethod && !selectedOffering.name.includes(capitalize(PricingType.FREE)));
    setAvailablePricingPlan(pricingOptions);
    setDefaultPricingPlanDetails(pricingOptions, selectedInvoiceGroupPricingPlan);
  };

  const checkForJiraProduct = isJiraProduct(entitlement?.productId);
  const checkForConfluenceProduct = isConfluenceProduct(entitlement?.productId);

  const productSkipTrialEligible = isProductSkipTrialEligible(offering?.productKey);
  const evaluateSkipTrialEligibilty = (selectedId: string | undefined) => {
    const selectedOffering = statuspage.availableOfferings?.find((offering: Offering) => offering?.id === selectedId);

    setIsSkipTrialChecked(false);
    setIsSkipTrialDisabled(!isSkipTrialEligibleV2(offering, selectedOffering ?? offering));
  };

  const setSkipTrial = async (checked: boolean) => {
    setIsSkipTrialChecked(checked);
  };

  const promotions = (scheduledChanges?.promotions
    ? scheduledChanges?.promotions
    : entitlement?.promotions ?? []) as Maybe<Promotion[]>;
  const appropriateAvailablePromotions = getAppropriatePromotions(availablePromotions, billingFrequency);
  const appropriatePromotions = getAppropriatePromotions(promotions, billingFrequency);
  const scheduledBillingFrequency = scheduledChanges?.pricingPlan?.billingFrequency ?? null;
  const defaultBillingFrequency: SelectItem = scheduledBillingFrequency
    ? { label: capitalize(scheduledBillingFrequency), value: toUpper(scheduledBillingFrequency) }
    : billingFrequency
    ? { label: capitalize(billingFrequency), value: toUpper(billingFrequency) }
    : ({} as SelectItem);
  const subscriptionId = entitlement?.subscriptionId ?? undefined;

  const setSelectedInvoiceGroupPricingPlanValue = (selectedPricingPlan: string | null | undefined) => {
    setSelectedInvoiceGroupPricingPlan(selectedPricingPlan);
    setDefaultPricingPlanDetails(availablePricingPlan, selectedPricingPlan);
  };

  const validateTextLength = (value: any) => value && (!(value.length < MAX_LENGTH) ? 'INVALID' : undefined);

  return (
    <>
      <FormSection>
        {showNoPaymentMethodWarning && (
          <InfoPanel
            type="warning"
            title="Cannot change plan - there are no payment methods on file for this entitlement"
            message={''}
          />
        )}
        {scheduledChanges && <InfoPanel type="information" message={InfoMessage.AMENDMENT_SCHEDULED} />}
      </FormSection>
      <FormSection>
        {pricingPlanFlag && <InfoPanel type="information" message={InfoMessage.PRICING_PLAN_ELIGIBILITY_WARNING} />}
      </FormSection>
      {!withReactivateEntitlementFlag && (
        <FormSection>
          <Field name="productId" defaultValue={entitlement.entitlementNumber} label="Product ID">
            {({ fieldProps }) => (
              <Textfield
                {...fieldProps}
                testId="product-id"
                isReadOnly
                name="productId"
                value={`${entitlement.entitlementNumber}`}
              />
            )}
          </Field>
        </FormSection>
      )}
      <FormSection>
        <table id="product-details" data-testid="product-offering-details" className="form-table-list">
          <tbody>
            <tr>
              <td colSpan={9}>
                <Field<ValueType<OfferingSelectItem>, HTMLSelectElement>
                  name="offeringSelectedItem"
                  defaultValue={defaultOfferingOption}
                  label="Plan"
                  isRequired
                >
                  {({ fieldProps }) => (
                    <div data-testid="product-offering-select">
                      <Select<OfferingSelectItem>
                        {...fieldProps}
                        options={availableOfferingOptions}
                        className="single-select"
                        classNamePrefix="offering-select"
                        testId="product-offering-details-options"
                        isDisabled={withReactivateEntitlementFlag}
                        //@ts-ignore
                        onChange={(selectedItem: OfferingSelectItem) => {
                          updateFieldBasedOnOffering(selectedItem.value.id);
                          evaluateSkipTrialEligibilty(selectedItem.value.id);
                          fieldProps.onChange(selectedItem);
                        }}
                      />
                    </div>
                  )}
                </Field>
              </td>
              {isUpdateUnitsFeatureEnabled && (
                <td colSpan={3}>
                  <Field name="offeringQuantity" label="Tier/Qty" defaultValue={defaultOfferingQuantity} isRequired>
                    {({ fieldProps }) => (
                      <>
                        {
                          <Textfield
                            {...fieldProps}
                            testId="offering-quantity"
                            isDisabled={withReactivateEntitlementFlag ? true : isUserTierDisabled}
                            value={defaultOfferingQuantity}
                            onChange={(event: FormEvent<HTMLInputElement>) => {
                              setDefaultOfferingQuantity(event.currentTarget.value);
                              fieldProps.onChange(event);
                            }}
                            type="number"
                            min="1"
                            max="99"
                          />
                        }
                      </>
                    )}
                  </Field>
                </td>
              )}
            </tr>
            {isEnabled(ENABLE_ENTITLEMENT_SKIP_TRIAL) && productSkipTrialEligible && (
              <tr>
                <td colSpan={12}>
                  <Field label="Skip trial" name="skipTrial">
                    {({ fieldProps }) => (
                      <Checkbox
                        {...fieldProps}
                        defaultChecked={isSkipTrialChecked}
                        isChecked={isSkipTrialChecked}
                        isDisabled={isSkipTrialDisabled}
                        onChange={(event: FormEvent<HTMLInputElement>) => {
                          setSkipTrial(event.currentTarget.checked);
                          fieldProps.onChange(event);
                        }}
                        testId="skipTrial"
                      />
                    )}
                  </Field>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </FormSection>
      {pricingPlanFlag && (
        <FormSection>
          <Field
            name="pricingPlan"
            defaultValue={defaultPricingPlan}
            isRequired
            id="pricing plan"
            label={'Pricing Plan'}
          >
            {({ fieldProps }) => (
              <RadioGroup {...fieldProps} options={availablePricingPlan} isDisabled={hasEligiblePricingPlan} />
            )}
          </Field>
        </FormSection>
      )}
      {billingFrequency && (
        <FormSection>
          <Field<ValueType<SelectItem>, HTMLSelectElement>
            label="Billing Frequency"
            name="billingFrequency"
            defaultValue={defaultBillingFrequency}
            isDisabled={checkForJiraProduct || checkForConfluenceProduct}
          >
            {({ fieldProps }) => (
              <Select<SelectItem>
                {...fieldProps}
                isSearchable={false}
                options={billingFrequencyOptions}
                onChange={(item: ValueType<SelectItem, false>) => {
                  setBillingFrequency(item?.value);
                  fieldProps.onChange(item);
                }}
              />
            )}
          </Field>
        </FormSection>
      )}
      {isEnabled(ENABLE_ENTITLEMENT_GRANT_PROMOTIONS) && withPromotionFlag && (
        <FormSection>
          <SelectPromotionFormField
            availablePromotions={appropriateAvailablePromotions}
            promotions={appropriatePromotions}
          />
        </FormSection>
      )}
      {isEnabled(ENABLE_SUPPORT_TICKET_AND_NOTE) && !withReactivateEntitlementFlag && (
        <FormSection>
          <Field name="supportTicketNumber" label="Support ticket number" validate={validateTextLength}>
            {({ fieldProps, error }) => (
              <>
                <Textfield
                  {...fieldProps}
                  placeholder={`Max limit: ${MAX_LENGTH} characters`}
                  testId="support-ticket-number"
                />
                {error && (
                  <ErrorMessage testId="support-ticket-number--max-char-error">{`Max limit: ${MAX_LENGTH} characters`}</ErrorMessage>
                )}
              </>
            )}
          </Field>
        </FormSection>
      )}
      {isEnabled(ENABLE_SUPPORT_TICKET_AND_NOTE) && !withReactivateEntitlementFlag && (
        <FormSection>
          <Field<string, HTMLTextAreaElement> label="Note" name="handNote" validate={validateTextLength}>
            {({ fieldProps, error }) => (
              <>
                <TextArea
                  placeholder={`Max limit: ${MAX_LENGTH} characters`}
                  {...fieldProps}
                  name="handNote"
                  testId="hand-note"
                />
                {error && (
                  <ErrorMessage testId="hand-note--max-char-error">{`Max limit: ${MAX_LENGTH} characters`}</ErrorMessage>
                )}
              </>
            )}
          </Field>
        </FormSection>
      )}
      {!withReactivateEntitlementFlag && (
        <FormSection>
          <InvoiceGroupFormField
            productId={entitlement?.productId ?? undefined}
            availableInvoiceGroups={availableInvoiceGroups}
            entitlementBillingInvoiceGroupId={defaultBillingGroupId}
            scheduledBillingInvoiceGroupId={scheduledChanges?.scheduledBillingInvoiceGroupId}
            setSelectedInvoiceGroupPricingPlanValue={setSelectedInvoiceGroupPricingPlanValue}
            isDisabled={!subscriptionId ? false : !!defaultBillingGroupId}
          />
        </FormSection>
      )}
    </>
  );
};
